import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { AlertCircle, Printer, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '@/components/ui/tooltip';
import type {
  WorkflowInstitution,
  WorkflowDocument,
  CorrespondenceValidation
} from '@/lib/services/correspondences/workflows/workflow-types';
import type { SendingStatus } from '../types';
import type { BatchActionItem } from '@/components/ui/workflow';
import { getInstitutionWarnings, getInstitutionBranding } from '../utils';
import { StatusIcon } from './status-icon';
import { SendStatusButton } from './send-status-button';
import { ConciergeBanner } from './concierge-banner';
import { PendingResearchBanner } from './pending-research-banner';
import { WarningAlert } from './warning-alert';
import { DocumentList } from './document-list';
import { AddDocument } from '@/components/shared/add-document';
import { workflowDocumentsToMatterFiles } from '@/lib/services/correspondences/workflows/workflow-document-to-matter-file';
import { convertDocumentsToWorkflowFormat } from '@/components/correspondence-chat/components/ChatContainerSimplified/utils/document-transform';
import { LetterEditorDialog } from './letter-editor-dialog';
import { EmailOnlyEditorDialog } from './email-only-editor-dialog';
import { InstitutionLogo } from './institution-logo';
import { useLawFirmProfile } from '@/lib/services/account/hooks';
import {
  SENDER_COPY_LABEL,
  useMatterSendsFromViewer
} from '@/lib/services/integrations/outlook';
import { getLawFirmLetterheadUrl } from '@/lib/utils/letterhead';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { getUserDisplayName } from '@/lib/utils/permissions';

export interface SignatoryUser {
  id: number;
  /** `law_firm_users.first_name` — nullable (an unaccepted invite has none). */
  firstName: string | null;
  lastName: string | null;
  email: string;
  role?: string;
  handwrittenSignatureFile?: { id: number } | null;
}

export interface InstitutionSendCardCorrespondence {
  item: BatchActionItem;
  letterContent: string;
  emailSubject?: string;
  emailBody?: string;
  additionalDocs: WorkflowDocument[];
  /** Documents already attached to correspondence */
  availableDocuments: WorkflowDocument[];
  /** All available matter documents for selection */
  matterDocuments?: WorkflowDocument[];
  correspondenceId?: number;
  /** Backend validation data */
  validation?: CorrespondenceValidation;
  /** Backend correspondence status */
  status?: string;
  /** Current emailto on the correspondence */
  emailTo?: string;
  /** Institution has no email — concierge research path */
  requiresEmailResearch?: boolean;
}

export interface InstitutionSendCardSignatoryState {
  /** Currently selected signatory id, as a string. Empty string when none. */
  current: string;
  /** Raw law firm user data from backend */
  options: SignatoryUser[];
  /** Hide the signatory dropdown entirely (e.g. follow-up workflow) */
  hidden?: boolean;
}

export interface InstitutionSendCardSelection {
  isSelected?: boolean;
  sendViaPostOnly?: boolean;
  sendSenderCopy?: boolean;
}

export interface InstitutionSendCardMode {
  followUpWorkflow?: boolean;
  collectionWorkflow?: boolean;
  /** Email-only mode hides the letter component */
  emailOnly?: boolean;
}

export interface InstitutionSendCardMatter {
  id: number;
  /** For email template subject/body defaults */
  deceasedName?: string;
  /** For email template subject/body defaults */
  reference?: string;
}

export interface InstitutionSendCardActions {
  onUpdateLetterContent: (
    institutionId: string,
    content: string,
    emailSubject?: string,
    emailBody?: string
  ) => void;
  onAddDocument: (institutionId: string, doc: WorkflowDocument) => void;
  onAddCorrespondenceAttachment?: (
    institutionId: string,
    doc: WorkflowDocument
  ) => void;
  onRemoveDocument: (institutionId: string, docId: number) => void;
  onRemoveCorrespondenceAttachment?: (
    institutionId: string,
    docId: number
  ) => void;
  onChangeSignatory: (institutionId: string, signatoryId: string) => void;
  onSend: (item: BatchActionItem) => void;
  onUploadDocument?: (
    institutionId: string,
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
    }>
  ) => Promise<void>;
  onToggle?: (institutionId: string, selected: boolean) => void;
  onToggleSendViaPostOnly?: (
    institutionId: string,
    sendViaPostOnly: boolean
  ) => void;
  onEmailToChange?: (institutionId: string, emailTo: string) => void;
  onSendSenderCopyChange?: (checked: boolean) => void;
}

export interface InstitutionSendCardProps {
  institution: WorkflowInstitution;
  correspondence: InstitutionSendCardCorrespondence;
  signatoryState: InstitutionSendCardSignatoryState;
  matter: InstitutionSendCardMatter;
  actions: InstitutionSendCardActions;
  selection?: InstitutionSendCardSelection;
  mode?: InstitutionSendCardMode;
}

/**
 * Institution send card using backend data directly
 */
export const InstitutionSendCard: React.FC<InstitutionSendCardProps> = ({
  institution,
  correspondence,
  signatoryState,
  matter,
  actions,
  selection,
  mode
}) => {
  const {
    item,
    letterContent,
    emailSubject,
    emailBody,
    additionalDocs,
    availableDocuments,
    matterDocuments,
    correspondenceId,
    validation,
    status: correspondenceStatus,
    emailTo: correspondenceEmailTo,
    requiresEmailResearch = false
  } = correspondence;

  const {
    current: signatory,
    options: signatories,
    hidden: hideSignatory = false
  } = signatoryState;

  const { id: matterId, deceasedName, reference: matterReference } = matter;

  const {
    onUpdateLetterContent,
    onAddDocument,
    onAddCorrespondenceAttachment,
    onRemoveDocument,
    onRemoveCorrespondenceAttachment,
    onChangeSignatory,
    onSend,
    onUploadDocument,
    onToggle,
    onToggleSendViaPostOnly,
    onEmailToChange,
    onSendSenderCopyChange
  } = actions;

  const {
    isSelected = true,
    sendViaPostOnly = false,
    sendSenderCopy
  } = selection ?? {};

  const {
    followUpWorkflow: isFollowUpWorkflow = false,
    collectionWorkflow: isCollectionWorkflow = false,
    emailOnly: showEmailOnly = false
  } = mode ?? {};

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [letterDialogOpen, setLetterDialogOpen] = React.useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [isPrintLoading, setIsPrintLoading] = React.useState(false);

  // Manual email entry for institutions with no email on file
  const [manualEmail, setManualEmail] = React.useState(
    correspondenceEmailTo || ''
  );
  const isManualEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualEmail);
  const hasPartialEmail =
    requiresEmailResearch &&
    manualEmail.trim().length > 0 &&
    !isManualEmailValid;
  const effectiveRequiresResearch =
    requiresEmailResearch && !isManualEmailValid;

  // Derived defaults for follow-up email mode.
  const defaultEmailSubject = React.useMemo(
    () =>
      showEmailOnly
        ? `Estate of ${deceasedName || '[Deceased Name]'} - Follow-up - ${matterReference || '[Matter Reference]'}`
        : '',
    [showEmailOnly, deceasedName, matterReference]
  );
  const defaultEmailBody = React.useMemo(
    () =>
      showEmailOnly
        ? `Dear Sir/Madam,\n\nRE: ${deceasedName || '[Deceased Name]'} (deceased) - Follow-up\n    Matter Reference: ${matterReference || '[Matter Reference]'}\n\nI am following up on my previous correspondence regarding the estate of the above-named deceased.\n\n[Please include follow-up details here]\n\nI would appreciate your response at your earliest convenience.\n\nKind regards,`
        : '',
    [showEmailOnly, deceasedName, matterReference]
  );

  // User overrides for inline email fields. `null` means "fall back to prop or
  // derived default", which preserves user edits across prop refreshes.
  const [userLocalEmailSubject, setUserLocalEmailSubject] = React.useState<
    string | null
  >(null);
  const [userLocalEmailBody, setUserLocalEmailBody] = React.useState<
    string | null
  >(null);
  const localEmailSubject =
    userLocalEmailSubject ?? (emailSubject || defaultEmailSubject);
  const localEmailBody = userLocalEmailBody ?? (emailBody || defaultEmailBody);
  const setLocalEmailSubject = (s: string) => setUserLocalEmailSubject(s);
  const setLocalEmailBody = (b: string) => setUserLocalEmailBody(b);

  // Get law firm profile for letterhead
  const { profile: lawFirmProfile } = useLawFirmProfile();
  // A self-copy is redundant only when the send leaves from the viewer's own
  // mailbox. Gating on "is Outlook active" hid it in shared-mailbox mode too,
  // where the sender has no copy anywhere they can see (VALE-1873).
  const sendsFromMe = useMatterSendsFromViewer(matter.id);

  const isPendingResearch = correspondenceStatus === 'pending-research-email';

  const warnings = getInstitutionWarnings(institution, availableDocuments);
  const branding = getInstitutionBranding(institution.name);
  const requiresWill =
    !isCollectionWorkflow &&
    institution.name.includes('NAB') &&
    !availableDocuments.some((doc) => doc.fileType === 'will');

  // Use backend validation instead of frontend logic
  const canSend = validation?.isValid ?? true;
  const validationErrors = validation?.errors ?? [];
  const validationWarnings = validation?.warnings ?? [];

  const hasValidSignature = !!signatories.find(
    (s) => s.id.toString() === signatory
  )?.handwrittenSignatureFile?.id;
  const status = item.status as SendingStatus;

  const handleSend = () => {
    // Only check for signature if signatory is not hidden (i.e., not a follow-up)
    if (!hideSignatory && !hasValidSignature) {
      alert('Please select a signatory with a valid signature');
      return;
    }
    onSend(item);
  };

  const handleLetterChange = (
    content: string,
    subject?: string,
    body?: string
  ) => {
    onUpdateLetterContent(institution.id.toString(), content, subject, body);
  };

  const handleEmailOnlyChange = (subject: string, body: string) => {
    // For email-only mode, we don't update letter content, just email fields
    onUpdateLetterContent(
      institution.id.toString(),
      letterContent,
      subject,
      body
    );
  };

  const handleAddAttachment = async (
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
    }>
  ) => {
    if (onUploadDocument) {
      try {
        await onUploadDocument(institution.id.toString(), files);
      } catch (error) {
        console.error(
          '[InstitutionSendCard] Error in onUploadDocument:',
          error
        );
        const { toast } = await import('@/lib/toast');
        toast.error(
          'Failed to upload attachment: ' +
            (error instanceof Error ? error.message : 'Unknown error')
        );
        throw error;
      }
    } else {
      // Upload files to matter first, then attach to correspondence
      try {
        // Import dependencies
        const { filesApi } = await import('@/lib/services/files/client');
        const { toast } = await import('@/lib/toast');

        for (const uploadFile of files) {
          // Upload file to matter as a general document
          // Note: We use 'additional-document' type for the upload, then attach to correspondence
          // The file becomes a "correspondence attachment" when attached, not by its upload type
          let uploadedFile;
          try {
            const { fileId } = await filesApi.uploadFile(
              matterId,
              uploadFile.file,
              'additional-document',
              { displayName: uploadFile.file.name }
            );
            // Upload-complete answers `{ fileId }` only; the row is read back
            // so the attachment carries a real id (VALE-1892).
            uploadedFile = await filesApi.getFile(matterId, fileId);
          } catch (error) {
            toast.error(
              'Failed to upload ' +
                uploadFile.file.name +
                ': ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
            throw error;
          }

          {
            // Convert to WorkflowDocument format
            // Handle fileType which can be string or object
            const fileTypeString =
              typeof uploadedFile.fileType === 'string'
                ? uploadedFile.fileType
                : uploadedFile.fileType?.slug || uploadFile.fileType;

            const workflowFile: WorkflowDocument = {
              id: uploadedFile.id,
              originalFilename:
                uploadedFile.originalFilename || uploadFile.file.name,
              displayName: uploadedFile.displayName,
              createdAt: uploadedFile.createdAt,
              updatedAt: uploadedFile.updatedAt,
              status: uploadedFile.status || 'available',
              s3Key: uploadedFile.s3Key,
              imageS3Key: uploadedFile.imageS3Key || null,
              imageMimeType: uploadedFile.imageMimeType || null,
              mimeType: uploadedFile.mimeType || uploadFile.file.type,
              fileType: fileTypeString,
              extractedData: uploadedFile.extractedData || null,
              isCertifiedCopy:
                uploadedFile.isCertifiedCopy || uploadFile.isCertifiedCopy,
              metadata: uploadedFile.metadata || null,
              analysisMetadata: uploadedFile.analysisMetadata || null,
              fileSizeKb:
                uploadedFile.fileSizeKb ||
                Math.ceil(uploadFile.file.size / 1024),
              deletedAt: uploadedFile.deletedAt || null
            };

            // Now attach to correspondence if handler available
            if (onAddCorrespondenceAttachment) {
              console.log(
                '[InstitutionSendCard] Attaching uploaded file to correspondence'
              );
              try {
                await onAddCorrespondenceAttachment(
                  institution.id.toString(),
                  workflowFile
                );
                toast.success(
                  `Attachment "${uploadFile.file.name}" added successfully`
                );
              } catch (attachError) {
                console.error(
                  '[InstitutionSendCard] Failed to attach file to correspondence:',
                  attachError
                );
                toast.error(
                  `Failed to attach "${uploadFile.file.name}": ` +
                    (attachError instanceof Error
                      ? attachError.message
                      : 'Unknown error')
                );
                throw attachError;
              }
            } else {
              // Fallback to local state if no correspondence handler
              console.warn(
                '[InstitutionSendCard] No correspondence handler, adding to local state only'
              );
              onAddDocument(institution.id.toString(), workflowFile);
              toast.warning(
                `Attachment "${uploadFile.file.name}" added to local state only - may not persist`
              );
            }
          }
        }
      } catch (error) {
        console.error(
          '[InstitutionSendCard] Error uploading attachment:',
          error
        );
        // Toast already shown above for specific errors
        throw error; // Re-throw to show error in dialog
      }
    }
  };

  const handleRemoveAttachment = (
    fileId: string,
    isCorrespondenceAttachment: boolean = false
  ) => {
    if (isCorrespondenceAttachment && onRemoveCorrespondenceAttachment) {
      onRemoveCorrespondenceAttachment(
        institution.id.toString(),
        parseInt(fileId)
      );
    } else {
      onRemoveDocument(institution.id.toString(), parseInt(fileId));
    }
  };

  // Attach existing matter documents to this institution's correspondence. All
  // documents must persist via onAddCorrespondenceAttachment (onAddDocument only
  // updates local state and would be dropped at send).
  const handleSelectExisting = async (docs: WorkflowDocument[]) => {
    const { toast } = await import('@/lib/toast');
    let successCount = 0;
    let failCount = 0;

    for (const doc of docs) {
      if (onAddCorrespondenceAttachment) {
        try {
          await onAddCorrespondenceAttachment(institution.id.toString(), doc);
          successCount++;
        } catch (error) {
          toast.error(
            `Failed to attach "${doc.originalFilename}": ` +
              (error instanceof Error ? error.message : 'Unknown error')
          );
          failCount++;
        }
      } else {
        // Fallback: only use local state if the correspondence handler is absent.
        onAddDocument(institution.id.toString(), doc);
        toast.warning(
          `"${doc.originalFilename}" added to local state only - may not persist`
        );
      }
    }

    if (docs.length > 1 && successCount > 0) {
      if (failCount === 0) {
        toast.success(`${successCount} attachment(s) added successfully`);
      } else {
        toast.warning(`${successCount} succeeded, ${failCount} failed`);
      }
    } else if (docs.length === 1 && successCount === 1) {
      toast.success(
        `Attachment "${docs[0].originalFilename}" added successfully`
      );
    }
  };

  const handleSignatoryChange = (signatoryId: string) => {
    onChangeSignatory(institution.id.toString(), signatoryId);
  };

  const handleDownloadCollectionDocuments = async () => {
    setIsPrintLoading(true);
    try {
      // Import the server action
      const { downloadCollectionDocumentsAction } =
        await import('@/lib/services/institution-forms/mutations');

      // Call the server action to get the download URL
      // Using correspondenceId for the new print-package endpoint
      const result = await downloadCollectionDocumentsAction(
        matterId,
        correspondenceId!
      );

      if (!result.success) {
        throw new Error(
          result.error || 'Failed to download collection documents'
        );
      }

      if (result.url) {
        // If we have a URL, fetch and download the file
        const response = await fetch(result.url);

        if (!response.ok) {
          throw new Error('Failed to download collection documents');
        }

        // Get the blob from the response
        const blob = await response.blob();

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `collection-documents-${institution.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading collection documents:', error);
      alert('Failed to download collection documents. Please try again.');
    } finally {
      setIsPrintLoading(false);
    }
  };

  const cardClassName = cn(
    'transition-all duration-200 border-2 overflow-hidden shadow-xs hover:shadow-md',
    {
      'border-success/40 bg-success-subtle': status === 'success',
      'border-error/40 bg-error-subtle': status === 'error',
      'bg-muted/50 opacity-75': status === 'loading',
      'border-border':
        status === 'pending' || status === 'pending-research-email'
    }
  );

  const handleToggle = (checked: boolean) => {
    if (onToggle) {
      onToggle(institution.id.toString(), checked);
    }
  };

  const handleToggleSendViaPostOnly = (checked: boolean) => {
    if (onToggleSendViaPostOnly) {
      onToggleSendViaPostOnly(institution.id.toString(), checked);
    }
  };

  const postOnlyTooltipText =
    'This correspondence has been marked to be sent via post only';

  const selectedSignatory = signatory
    ? signatories.find((s) => s.id.toString() === signatory)
    : undefined;
  const selectedSignatoryName = selectedSignatory
    ? getUserDisplayName(selectedSignatory)
    : undefined;

  return (
    <Card className={cn(cardClassName, 'relative')}>
      <CardHeader className="relative pb-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div>
                {sendViaPostOnly ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Checkbox
                            className="me-2"
                            checked={isSelected}
                            disabled
                            onClick={(e) => e.stopPropagation()}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{postOnlyTooltipText}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : !isFollowUpWorkflow ? (
                  <Checkbox
                    className="me-2"
                    checked={isSelected}
                    onCheckedChange={handleToggle}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : null}
              </div>

              <InstitutionLogo
                name={institution.name}
                faviconUrl={institution.faviconUrl}
                size="sm"
              />
              {/*
                VALE-531: the brand stripe is painted from the institution's own
                brand hex rather than a hand-derived Tailwind class pair. See
                `../constants.ts` — six of thirteen brands had collapsed onto two
                semantic roles, and every stop pair onto one colour. Brand
                colour is theme-invariant by design.
              */}
              <div
                className="h-4 w-0.5 rounded-full"
                style={{ background: branding.primary }}
              />
              <CardTitle className="text-sm">{institution.name}</CardTitle>
              {requiresWill && (
                <StatusBadge tone="caution" icon={AlertCircle}>
                  Will Required
                </StatusBadge>
              )}
              <StatusIcon status={status} />
            </div>
          </div>

          {/* Action Button and Signatory */}
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            {/* Send via post only checkbox for collection workflow */}
            {isCollectionWorkflow &&
              status !== 'success' &&
              !isPendingResearch && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`send-via-post-only-${institution.id}`}
                    checked={sendViaPostOnly}
                    onCheckedChange={handleToggleSendViaPostOnly}
                  />
                  <label
                    htmlFor={`send-via-post-only-${institution.id}`}
                    className="text-sm leading-none font-medium whitespace-nowrap"
                  >
                    Send via post only
                  </label>
                </div>
              )}
            {!hideSignatory && (
              <Select
                value={signatory}
                onValueChange={handleSignatoryChange}
                disabled={status === 'loading' || status === 'success'}
              >
                <SelectTrigger className="h-8 w-full sm:w-[220px]">
                  <SelectValue placeholder="Select signatory">
                    {signatory && selectedSignatoryName}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {signatories.map((sig) => (
                    <SelectItem key={sig.id} value={sig.id.toString()}>
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {getUserDisplayName(sig)}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {sig.role}
                          {!sig.handwrittenSignatureFile?.id &&
                            ' (no signature)'}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Print and Send buttons for collection workflow */}
            {isCollectionWorkflow && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="xs"
                      density="compact"
                      gap="standard"
                      variant={
                        institution.hardCopyRequired ? 'default' : 'outline'
                      }
                      onClick={handleDownloadCollectionDocuments}
                      disabled={status === 'loading' || isPrintLoading}
                    >
                      {isPrintLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Printer className="h-3 w-3" />
                      )}
                      {isPrintLoading ? 'Downloading...' : 'Print Package'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Download all collection documents as PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Bcc me a copy */}
            {onSendSenderCopyChange &&
              !isPendingResearch &&
              !sendsFromMe &&
              status !== 'success' && (
                <div className="me-2 flex items-center space-x-2">
                  <Checkbox
                    id={`receive-copy-${institution.id}`}
                    checked={sendSenderCopy}
                    disabled={status === 'loading'}
                    onCheckedChange={(checked) =>
                      onSendSenderCopyChange(checked as boolean)
                    }
                  />
                  <label
                    htmlFor={`receive-copy-${institution.id}`}
                    className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {SENDER_COPY_LABEL}
                  </label>
                </div>
              )}
            {/* Send button with conditional styling for collection workflow */}
            {isPendingResearch ? null : isCollectionWorkflow ? (
              <Button
                size="xs"
                density="compact"
                gap="standard"
                variant={institution.hardCopyRequired ? 'outline' : 'default'}
                onClick={handleSend}
                disabled={
                  !canSend ||
                  status === 'success' ||
                  status === 'loading' ||
                  hasPartialEmail
                }
              >
                {status === 'loading' && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {status === 'success'
                  ? 'Sent'
                  : status === 'loading'
                    ? sendViaPostOnly
                      ? 'Sending...'
                      : effectiveRequiresResearch
                        ? 'Submitting...'
                        : 'Sending...'
                    : sendViaPostOnly
                      ? 'Send'
                      : effectiveRequiresResearch
                        ? 'Ready to Send'
                        : 'Send'}
              </Button>
            ) : (
              <SendStatusButton
                status={status}
                onSend={handleSend}
                disabled={!canSend || hasPartialEmail}
                requiresResearch={effectiveRequiresResearch}
              />
            )}
          </div>
        </div>
      </CardHeader>

      {/* Concierge Banner - shown when institution has no email */}
      {requiresEmailResearch &&
        !isPendingResearch &&
        !sendViaPostOnly &&
        status !== 'success' && (
          <CardContent className="pt-0 pb-6">
            <ConciergeBanner
              isCollectionWorkflow={isCollectionWorkflow}
              email={manualEmail}
              onEmailChange={(email) => {
                setManualEmail(email);
                onEmailToChange?.(institution.id.toString(), email);
              }}
            />
          </CardContent>
        )}

      {/* Locked State Banner - shown when pending email research */}
      {isPendingResearch && (
        <CardContent className="pt-0 pb-6">
          <PendingResearchBanner />
        </CardContent>
      )}

      {/* Email-only inline fields or standard attachments list */}
      {status !== 'success' && (
        <CardContent className="pt-0">
          {showEmailOnly ? (
            /* Inline email fields for email-only mode */
            <div className="space-y-4">
              {/* Email Subject */}
              <div className="space-y-2">
                <Label htmlFor={`email-subject-${institution.id}`}>
                  Subject
                </Label>
                <Input
                  id={`email-subject-${institution.id}`}
                  value={localEmailSubject}
                  onChange={(e) => {
                    const newSubject = e.target.value;
                    setLocalEmailSubject(newSubject);
                    onUpdateLetterContent(
                      institution.id.toString(),
                      letterContent || '',
                      newSubject,
                      localEmailBody
                    );
                  }}
                  placeholder="Enter email subject..."
                  className="w-full"
                />
              </div>

              {/* Email Body */}
              <div className="space-y-2">
                <Label htmlFor={`email-body-${institution.id}`}>Message</Label>
                <Textarea
                  id={`email-body-${institution.id}`}
                  value={localEmailBody}
                  onChange={(e) => {
                    const newBody = e.target.value;
                    setLocalEmailBody(newBody);
                    onUpdateLetterContent(
                      institution.id.toString(),
                      letterContent || '',
                      localEmailSubject,
                      newBody
                    );
                  }}
                  placeholder="Type your email message..."
                  className="min-h-[200px] resize-y font-sans"
                  rows={10}
                />
              </div>

              {/* Attachments section */}
              <div className="border-t pt-4">
                <DocumentList
                  documents={availableDocuments}
                  additionalDocs={additionalDocs}
                  institutionName={institution.name}
                  matterId={matterId}
                  correspondenceId={correspondenceId}
                  letterContent={letterContent}
                  layout="row"
                  onAddAttachment={() => setUploadDialogOpen(true)}
                  onRemoveAttachment={handleRemoveAttachment}
                  onLetterClick={() => {}}
                  hideLetterComponent={true}
                  showEmailComponent={false}
                  emailSubject={localEmailSubject}
                  emailBody={localEmailBody}
                  readOnly={isPendingResearch}
                />
              </div>
            </div>
          ) : (
            /* Standard document list with Letter for non-email mode */
            <DocumentList
              documents={availableDocuments}
              additionalDocs={additionalDocs}
              institutionName={institution.name}
              matterId={matterId}
              correspondenceId={correspondenceId}
              letterContent={letterContent}
              layout="row"
              onAddAttachment={() => setUploadDialogOpen(true)}
              onRemoveAttachment={handleRemoveAttachment}
              onLetterClick={() => setLetterDialogOpen(true)}
              hideLetterComponent={false}
              showEmailComponent={false}
              emailSubject={emailSubject}
              emailBody={emailBody}
              readOnly={isPendingResearch}
            />
          )}
        </CardContent>
      )}

      {/* Backend Validation Errors */}
      {validationErrors.length > 0 && !isPendingResearch && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {validationErrors.map((error) => (
              <Banner key={error} tone="critical" title={error} />
            ))}
          </div>
        </CardContent>
      )}

      {/* Backend Validation Warnings */}
      {validationWarnings.length > 0 && !isPendingResearch && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {validationWarnings.map((warning) => (
              <Banner key={warning} tone="caution" title={warning} />
            ))}
          </div>
        </CardContent>
      )}

      {/* Legacy Frontend Warnings (keep for now for non-validation warnings) */}
      {warnings.length > 0 && !isPendingResearch && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {warnings.map((warning, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- legacy workflow warnings shape varies; will be removed when migrated to backend validation
              <WarningAlert key={index} warning={warning} />
            ))}
          </div>
        </CardContent>
      )}

      {/* Add attachment — shared AddDocument, opened (controlled) from the
          DocumentList "Add attachment" action. Upload lands as a
          correspondence-attachment (parity with the retired AddAttachmentDialog). */}
      <AddDocument
        matterId={matterId}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        sources={['existing', 'upload', 'pms']}
        files={workflowDocumentsToMatterFiles(
          matterDocuments || availableDocuments
        )}
        multiple
        alreadySelectedFileIds={[...availableDocuments, ...additionalDocs].map(
          (d) => d.id
        )}
        onPickExisting={(ids, pickedFiles) => {
          // Attach the documents AddDocument resolved rather than re-finding them
          // in the matter-documents pool: a PMS import is brand new, so that
          // lookup would come back empty and silently attach nothing (LF-6261).
          void handleSelectExisting(
            convertDocumentsToWorkflowFormat([...pickedFiles])
          );
          return ids;
        }}
        onUpload={async (files) => {
          await handleAddAttachment(
            files.map((file) => ({
              file,
              fileType: 'correspondence-attachment',
              isCertifiedCopy: false
            }))
          );
          return [];
        }}
      />

      {/* Letter Editor Dialog */}
      {!showEmailOnly && (
        <LetterEditorDialog
          open={letterDialogOpen}
          onOpenChange={setLetterDialogOpen}
          letterContent={letterContent}
          emailSubject={emailSubject}
          emailBody={emailBody}
          onSave={handleLetterChange}
          institutionName={institution.name}
          institutionId={institution.id}
          matterId={matterId}
          correspondenceId={correspondenceId}
          lawFirmLetterheadUrl={getLawFirmLetterheadUrl(
            lawFirmProfile?.letterHeadFileId
          )}
        />
      )}

      {/* Email Only Editor Dialog */}
      {showEmailOnly && (
        <EmailOnlyEditorDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          emailSubject={emailSubject}
          emailBody={emailBody}
          onSave={handleEmailOnlyChange}
          institutionName={institution.name}
          institutionId={institution.id}
          matterId={matterId}
          correspondenceId={correspondenceId}
          deceasedName={deceasedName}
          matterReference={matterReference}
        />
      )}
    </Card>
  );
};
